import { LitElement, css, html, nothing } from "lit";
import { customElement, property, query } from "lit/decorators.js";

/**
 * Small on/off toggle switch (pill + knob) styled for the dark
 * fleet theme. Used in the search-options popover instead of a
 * native checkbox so the panel can be narrower and the affordance
 * reads as a "switch", not as a form input.
 *
 * - `checked` mirrors the underlying boolean state.
 * - `disabled` greys the track and blocks the click.
 * - Emits a `change` event whose `detail.checked` carries the new
 *   state, so callers don't need to read the DOM.
 * - Also dispatches a bubbling `change` event with the same
 *   `detail` so existing call-sites that listen for `change` on
 *   the toggle element keep working (we attach a hidden
 *   `<input type="checkbox">` inside and re-dispatch from it).
 */
@customElement("fleet-toggle")
export class FleetToggle extends LitElement {
  // `reflect: true` mirrors the boolean value onto the host's
  // attribute so the `:host([checked])` selector in `static
  // styles` (which paints the ON colour + moves the knob right)
  // matches when `this.checked` flips. Without reflection Lit
  // only updates the JS property and the CSS selector never
  // matches — operator saw the toggle "not moving" even though
  // `change` events fired correctly behind the scenes.
  @property({ type: Boolean, reflect: true }) checked = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: String }) label = "";

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: inherit;
      user-select: none;
      cursor: pointer;
    }
    :host([disabled]) {
      opacity: 0.45;
      cursor: default;
      pointer-events: none;
    }
    button.fleet-toggle__track {
      all: unset;
      position: relative;
      flex: 0 0 auto;
      width: 28px;
      height: 16px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.18);
      transition: background 140ms ease-out;
      cursor: pointer;
      box-sizing: border-box;
    }
    button.fleet-toggle__track::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #f5f7fa;
      transition: transform 140ms ease-out;
    }
    :host([checked]) button.fleet-toggle__track {
      background: var(--fleet-cyan, #00aaff);
    }
    :host([checked]) button.fleet-toggle__track::after {
      transform: translateX(12px);
    }
    button.fleet-toggle__track:focus-visible {
      outline: 1px solid var(--fleet-cyan, #00aaff);
      outline-offset: 2px;
    }
    .fleet-toggle__label {
      line-height: 1;
      white-space: nowrap;
    }
  `;

  private toggle(): void {
    if (this.disabled) return;
    this.checked = !this.checked;
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { checked: this.checked },
        composed: true,
        bubbles: true,
      }),
    );
  }

  /**
   * Host-level click handler. Makes the whole toggle (track +
   * label area) clickable, so a click on the label text also
   * flips the switch. The track button has its own click handler
   * — we skip propagation here when the click originated on (or
   * inside) the track, otherwise every click would fire twice.
   *
   * `composedPath()` lets us look across the shadow boundary so
   * a click on the inner track button is detected even though it
   * originates in shadow DOM.
   */
  private onHostClick(e: Event): void {
    if (this.disabled) return;
    const path = e.composedPath();
    if (path.includes(this.trackEl)) return;
    this.toggle();
  }

  @query("button.fleet-toggle__track")
  private trackEl!: HTMLButtonElement;

  connectedCallback(): void {
    super.connectedCallback();
    // Listen on the host so clicks on the label area (outside
    // the inner shadow-DOM track button) still toggle the
    // switch. The track button's own `@click` continues to fire
    // for direct clicks on the pill — `onHostClick` skips the
    // toggle if the click path includes the track element.
    this.addEventListener("click", this.onHostClick);
  }

  disconnectedCallback(): void {
    this.removeEventListener("click", this.onHostClick);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <button
        type="button"
        class="fleet-toggle__track"
        role="switch"
        aria-checked=${this.checked ? "true" : "false"}
        aria-label=${this.label || "Toggle"}
        ?disabled=${this.disabled}
        @click=${this.toggle}
      ></button>
      ${this.label ? html`<span class="fleet-toggle__label">${this.label}</span>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "fleet-toggle": FleetToggle;
  }
}